// src/components/resources/calculators/ROASCalculator.logic.ts
//
// Pure calculation logic for the ROAS calculator + its intent-qualified
// landing variants. Lives in a sibling module (no `'use client'`) so the
// math is unit-testable in isolation — mirrors the
// `MarketingBudgetCalculator.logic.ts` split. The component imports these
// types + `calculateMetrics`; see `ROASCalculator.tsx` for the UI.

export type BusinessType = 'ecommerce' | 'b2b'

/**
 * Which calculation the tool performs, set by the landing-variant preset
 * (the umbrella + eCommerce/B2B variants use `'roas'`):
 *   - `'roas'`   realized ROAS only (the historical behavior).
 *   - `'roi'`    adds a gross-margin input and surfaces a profit-based
 *                Marketing ROI %, with a COGS-aware Net Profit (so the
 *                `ad-spend-roi-calculator` page's tool matches its copy).
 *   - `'target'` adds gross-margin + target-margin inputs and surfaces the
 *                break-even and profit-target ROAS the margins require
 *                (so the `target-roas-calculator` page computes what it
 *                teaches), alongside the realized ROAS for comparison.
 */
export type ROASCalculatorMode = 'roas' | 'roi' | 'target'

export interface CalculatorInputs {
  businessType: BusinessType
  adSpend: number
  cpc: number
  conversionRate: number
  averageValue: number
  leadToCustomerRate?: number // Only for B2B
  /** Gross profit margin on revenue (%). Used by `'roi'`/`'target'` modes
   *  to compute COGS-aware profit, ROI, and break-even/target ROAS. */
  grossMargin: number
  /** Desired net margin on ad-driven revenue (%), `'target'` mode only —
   *  the profit-target ROAS is the ROAS that leaves this much margin after
   *  COGS and ad spend. */
  targetProfitMargin: number
}

export interface CalculatorResults {
  clicks: number
  conversions: number
  costPerLead: number
  leadValue: number
  revenue: number
  profit: number
  roas: number
  cpa: number
  /** The inputs this result was computed from, captured at calculate time so a
   *  later edit to the live form can't desync the emailed/CRM-stored payload
   *  from the visible result card. */
  inputs: CalculatorInputs
  /** Mode captured at calculate time, so the result cards always match the
   *  calculation that produced them even if a prop changes before recalc. */
  mode: ROASCalculatorMode
  /** COGS-aware net profit (revenue × margin − ad spend). `'roi'`/`'target'`
   *  modes only; the displayed "Net Profit" card. */
  netProfit?: number
  /** Marketing ROI % = net profit ÷ ad spend × 100. `'roi'`/`'target'` only. */
  roi?: number
  /** Break-even ROAS = 1 ÷ gross margin. `'target'` mode, plus the umbrella
   *  `'roas'` mode when the optional gross-margin input is set (> 0). */
  breakEvenRoas?: number
  /** Target net margin (%) captured at calculate time, so the "Target ROAS
   *  (N% margin)" label always reflects the margin the displayed `targetRoas`
   *  was computed for — not a since-edited live input. `'target'` mode only. */
  targetProfitMargin?: number
  /** Profit-target ROAS = 1 ÷ (gross margin − target margin). `'target'` only.
   *  Non-finite (→ shown as "—") when the target margin meets/exceeds the
   *  gross margin, which is unachievable at any ROAS. */
  targetRoas?: number
  /** Business type captured at calculate time, so the benchmark segment always
   *  matches this result even if the live toggle changes before recalculating. */
  businessType: BusinessType
}

/**
 * Derive every displayed metric from the raw inputs. In the default `'roas'`
 * mode, margin math is opt-in: with a zero/empty gross margin the result is
 * byte-identical to the historical behavior, while a margin > 0 additionally
 * surfaces break-even ROAS and COGS-aware net profit so the realized ROAS can
 * be judged against the page's own "compare to break-even" guidance. The
 * `'roi'`/`'target'` modes always use the gross-margin input to compute
 * COGS-aware profit/ROI and the break-even/target ROAS the margins require.
 *
 * Non-finite results (e.g. a zeroed ad-spend/CPC → divide-by-zero, or a target
 * margin ≥ gross margin) are returned as Infinity/NaN; the component renders
 * those as an em dash rather than "Infinityx" / "$NaN".
 */
/** Clamp a percentage input to the valid 0–100 range. */
function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.min(100, Math.max(0, value))
}

export function calculateMetrics(inputs: CalculatorInputs, mode: ROASCalculatorMode): CalculatorResults {
  const clicks = inputs.adSpend / inputs.cpc
  const conversions = clicks * (inputs.conversionRate / 100)

  let revenue: number
  // Apply the B2B lead→customer multiplier whenever the B2B tab is active and a
  // rate is present — including an explicit 0% close rate. Gating on truthiness
  // would treat 0 as "no rate" and silently fall back to the eCommerce formula,
  // reporting positive revenue/ROAS for a campaign that closed nobody.
  if (inputs.businessType === 'b2b' && inputs.leadToCustomerRate !== undefined) {
    revenue = conversions * (inputs.leadToCustomerRate / 100) * inputs.averageValue
  } else {
    revenue = conversions * inputs.averageValue
  }

  const profit = revenue - inputs.adSpend
  const roas = revenue / inputs.adSpend
  const costPerLead = inputs.adSpend / conversions
  const leadValue = revenue / conversions

  const base: CalculatorResults = {
    inputs: { ...inputs },
    clicks: Math.round(clicks),
    conversions: Math.round(conversions),
    costPerLead,
    leadValue,
    revenue,
    profit,
    roas,
    cpa: costPerLead,
    mode,
    businessType: inputs.businessType
  }

  if (mode === 'roas') {
    // Optional margin on the umbrella: break-even ROAS + COGS-aware net
    // profit only. ROI/target metrics stay exclusive to their variant pages.
    const roasMargin = clampPercent(inputs.grossMargin) / 100
    if (roasMargin > 0) {
      return {
        ...base,
        breakEvenRoas: 1 / roasMargin,
        netProfit: revenue * roasMargin - inputs.adSpend,
      }
    }
    return base
  }

  // ROI / target modes: a gross-margin input is present, so we can compute
  // COGS-aware profit (not just revenue − ad spend) and a true ROI. Clamp the
  // margins to a valid 0–100%: you can't keep more than 100% of revenue as gross
  // profit, and a negative entry is meaningless. Without this, e.g. a 150% gross
  // margin would inflate Net Profit/ROI and yield an impossible sub-1x break-even
  // ROAS (1 / 1.5 = 0.67x); a 0% (or negative→0) margin is treated as
  // "unreachable" below.
  const margin = clampPercent(inputs.grossMargin) / 100
  const grossProfit = revenue * margin
  const netProfit = grossProfit - inputs.adSpend
  // Marketing ROI: profit (net of COGS and ad spend) per dollar of ad spend.
  const roi = (netProfit / inputs.adSpend) * 100

  if (mode === 'roi') {
    return { ...base, netProfit, roi }
  }

  // target mode: the ROAS the margins require, independent of the realized
  // funnel above (which is shown alongside for comparison).
  //   break-even ROAS = 1 / margin                 (gross profit == ad spend)
  //   target ROAS     = 1 / (margin − targetMargin) (leaves targetMargin of
  //                     revenue as profit after COGS and ad spend)
  const targetMargin = clampPercent(inputs.targetProfitMargin) / 100
  const targetSpread = margin - targetMargin
  // A non-positive gross margin can't break even or hit a target at ANY ROAS
  // (the product loses money on every sale before ad spend), and a target margin
  // at/above the gross margin is likewise unachievable. Emit a non-finite value
  // in those cases so the metric renderer shows an em dash instead of a
  // misleading negative ROAS that the comparison text would call "profitable".
  const breakEvenRoas = margin > 0 ? 1 / margin : Number.POSITIVE_INFINITY
  const targetRoas = margin > 0 && targetSpread > 0 ? 1 / targetSpread : Number.POSITIVE_INFINITY

  return { ...base, netProfit, roi, breakEvenRoas, targetRoas, targetProfitMargin: inputs.targetProfitMargin }
}
